import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCurrentAccount } from '@cube3/state/src/redux/components/Administration/withCurrentUserAccount';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import {
  ExportChannelsPicker,
  useExportChannelsStyles
} from '@cube3/ui/src/exports/ExportChannelsPicker';
import {
  TikTokCreatorInfo,
  TikTokConfig
} from '@cube3/common/model/resource-types';
import { ExportTarget } from '@cube3/common/model/schema/resources/export-target';
import { ModalMenuUI } from '@cube3/ui/src/Modal/ModalMenuUI';
import Button from '@cube3/ui/src/Buttons/Button';
import { useModalActions } from '../../Modals/modalActions';
import { hasResolved } from '@cube3/state/src/redux/ducks/request-status/utils/hasResolved';

const emptyArray = [];

export const TikTokAccountsModal = (props) => {
  const { modalContext } = props;
  const { exportTargetTypeId } = modalContext;

  const [selectedAccount, setSelectedAccount] =
    useState<ExportTarget<'tiktok'>>(null);

  const [selectedCreatorInfo, setSelectedCreatorInfo] =
    useState<TikTokCreatorInfo>(null);

  const { previousModal, openModal } = useModalActions();
  const [currentAccountId] = useCurrentAccount();

  const params = useMemo(() => {
    return {
      filter: {
        exportTargetTypeId: exportTargetTypeId // NOTE: not implemented
      }
    };
  }, [exportTargetTypeId]);

  const {
    resources: exportTargets,
    status: exportTargetStatus
  }: { resources?: ExportTarget<'tiktok'>[]; status?: string } =
    useResourceList__ALPHA({
      resourceId: currentAccountId,
      resourceType: 'account',
      edgeType: 'export-target',
      edgeLabel: 'export-targets',
      params,
      strategy: 'fetch-on-mount'
    });

  const creator_info =
    (selectedAccount &&
      selectedAccount.attributes.integration_config.creator_info) ||
    emptyArray;

  const handleNext = useCallback(() => {
    openModal(
      'social_export_options',
      {
        creator_info: selectedCreatorInfo,
        exportTargetId: selectedAccount?.id,
        exportTargetTypeId: exportTargetTypeId,
        platform: 'TikTok'
      },
      false
    );
  }, [openModal, selectedAccount, selectedCreatorInfo, exportTargetTypeId]);

  const handleManageIntegration = useCallback(() => {
    // open modal version of Account’s Integrations page
    openModal('connected_accounts_modal', null, false);
  }, [openModal]);

  const handleSelectAccount = useCallback((account) => {
    setSelectedAccount(account);
  }, []);

  useEffect(() => {
    if (!selectedAccount && exportTargets?.length > 0) {
      setSelectedAccount(exportTargets[0]);
    }
    if (
      selectedAccount &&
      !exportTargets.find((ac) => ac.id === selectedAccount?.id)
    ) {
      setSelectedAccount(null);
    }
  }, [exportTargets, selectedAccount]);

  useEffect(() => {
    if (creator_info.length === 1) {
      setSelectedCreatorInfo(creator_info[0]);
    }
    if (creator_info.length === 0) {
      setSelectedCreatorInfo(null);
    }
  }, [creator_info]);

  const classes = useExportChannelsStyles();

  return (
    <ModalMenuUI
      title="Export to TikTok • Select Account"
      loading={!hasResolved(exportTargetStatus)}
      onCloseEvent={previousModal}
      classes={{ contents: classes.modalContents }}
      wide={true}
      footerLeftComponent={
        <Button
          text={'Manage integration'}
          onClick={handleManageIntegration}
          colorVariant={'ghost1'}
        />
      }
      footerRightComponent={
        <>
          <Button
            text={'Cancel'}
            colorVariant={'ghost2'}
            onClick={previousModal}
            disabled={false}
          />
          <Button
            disabled={false}
            colorVariant={'filled1'}
            text={'Next'}
            onClick={handleNext}
          />
        </>
      }
    >
      {hasResolved(exportTargetStatus) && (
        <ExportChannelsPicker<
          ExportTarget<'tiktok'>,
          TikTokConfig['creator_info'][number]
        >
          title="TikTok Space"
          noChannelsMessage="Click Next to fill in the details of your export."
          accounts={exportTargets}
          channels={creator_info}
          selectedAccount={selectedAccount}
          selectedChannel={undefined}
          onManageIntegration={handleManageIntegration}
          onSelectAccount={handleSelectAccount}
          onSelectChannel={setSelectedCreatorInfo}
        />
      )}
    </ModalMenuUI>
  );
};
